import { useEffect, useState } from "react";

import { useTask } from "../../../hooks/query";
import { mapTaskAssetToImage, TaskListAsset } from "../../../utils/taskAssetImages";
import { ImageWithMetadata } from "../../../constants/inspectionPhotos";
import { AlbumGroup, AlbumSectionDefinition } from "../types";

interface UseAlbumGroupsOptions {
    sections: AlbumSectionDefinition[];
    taskId: string;
    assetType: string;
}

function buildGroups(sections: AlbumSectionDefinition[], assets: TaskListAsset[]): AlbumGroup[] {
    return sections.map((section) => ({
        ...section,
        images: assets
            .filter((asset) => asset.Label === section.label)
            .map((asset) => mapTaskAssetToImage(asset)),
    }));
}

export function useAlbumGroups(props: UseAlbumGroupsOptions) {
    const { sections, taskId, assetType } = props;
    const { useListAssets } = useTask();
    const listAssetsSvc = useListAssets();
    const [groups, setGroups] = useState<AlbumGroup[]>(() =>
        sections.map((section) => ({ ...section, images: [] })),
    );
    const [loading, setLoading] = useState(true);

    const fetchGroups = async () => {
        if (!taskId) {
            setGroups(sections.map((section) => ({ ...section, images: [] })));
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await listAssetsSvc.mutateAsync({
                TaskId: taskId,
                AssetType: assetType,
            });
            setGroups(buildGroups(sections, response));
        } catch (error) {
            console.error("Failed to fetch album assets", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, [taskId, assetType]);

    const updateGroupImages = (label: string, images: ImageWithMetadata[]) => {
        setGroups((prev) =>
            prev.map((group) => (group.label === label ? { ...group, images } : group)),
        );
    };

    return {
        groups,
        loading,
        refreshGroups: fetchGroups,
        updateGroupImages,
    };
}
